<template>
  <div class="delay-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <i class="fas fa-clock"></i>
        <span>Délai</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="delay-content">
      <div class="delay-info">
        <div class="timer-display">
          <i class="fas fa-hourglass-half"></i>
          <span class="timer-value">{{ formattedDelay }}</span>
        </div>
        <p class="delay-description">Le bot attendra {{ formattedDelay }} avant de continuer</p>
      </div>
      
      <div class="delay-control">
        <label class="control-label">Durée d'attente :</label>
        <div class="slider-container">
          <input 
            type="range" 
            class="delay-slider" 
            :value="sliderValue"
            :min="minValue"
            :max="maxValue"
            :step="step"
            :style="sliderStyle"
            @input="onSliderChange"
            @change="onSliderChange"
          />
          <div class="slider-marks">
            <span class="mark">{{ formatValue(minValue) }}</span>
            <span class="mark">{{ formatValue(maxValue) }}</span>
          </div>
        </div>
        <div class="quick-presets">
          <button 
            v-for="preset in presets" 
            :key="preset.value"
            class="preset-btn"
            :class="{ 'active': currentValue === preset.value }"
            @click="setPreset(preset.value)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DelayElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      // Valeurs du slider (en secondes)
      minValue: 1,   // 1 seconde
      maxValue: 5,   // 5 secondes
      step: 0.1,     // Pas de 0.1 secondes
      presets: [
        { label: '1s', value: 1 },
        { label: '2s', value: 2 },
        { label: '3s', value: 3 },
        { label: '4s', value: 4 },
        { label: '5s', value: 5 }
      ]
    }
  },
  computed: {
    currentValue() {
      const timer = this.element.timmer || '1';
      return parseFloat(timer) || 1; // Par défaut 1 seconde
    },
    
    sliderValue() {
      return Math.max(this.minValue, Math.min(this.maxValue, this.currentValue));
    },
    
    formattedDelay() {
      const timerValue = this.currentValue;
      
      if (isNaN(timerValue) || timerValue === 0) {
        return '1 seconde';
      }
      
      if (timerValue === 1) {
        return '1 seconde';
      } else {
        return `${timerValue} secondes`;
      }
    },
    
    sliderStyle() {
      const percentage = ((this.sliderValue - this.minValue) / (this.maxValue - this.minValue)) * 100;
      return {
        background: `linear-gradient(to right, #059669 0%, #059669 ${percentage}%, #d1d5db ${percentage}%, #d1d5db 100%)`
      };
    }
  },
  methods: {
    onSliderChange(event) {
      const newValue = parseFloat(event.target.value);
      this.updateDelay(newValue);
    },
    
    setPreset(value) {
      this.updateDelay(value);
    },
    
    updateDelay(value) {
      const updatedElement = { 
        ...this.element, 
        timmer: value.toString() 
      };
      this.$emit('update', updatedElement);
    },
    
    formatValue(value) {
      return `${value}s`;
    }
  }
}
</script>

<style scoped>
.delay-element-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.delay-element-wrapper:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #059669;
  font-size: 0.875rem;
}

.element-type-badge i {
  color: #059669;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.delay-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.delay-info {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-radius: 10px;
  border: 1px solid #bbf7d0;
}

.timer-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: 50px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  min-width: 120px;
}

.timer-display i {
  color: #059669;
  font-size: 1.2rem;
}

.timer-value {
  font-weight: 700;
  color: #065f46;
  font-size: 1rem;
}

.delay-description {
  margin: 0;
  color: #047857;
  font-weight: 500;
  font-size: 0.95rem;
}

.delay-control {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.control-label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.slider-container {
  position: relative;
  margin-bottom: 12px;
}

.delay-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delay-slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
  transition: all 0.2s ease;
}

.delay-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
}

.delay-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
  transition: all 0.2s ease;
}

.delay-slider::-moz-range-thumb:hover {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
}

.slider-marks {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding: 0 10px;
}

.mark {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.quick-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  background: white;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.preset-btn:hover {
  border-color: #059669;
  color: #059669;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.preset-btn.active {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border-color: #059669;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

.preset-btn.active:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
}
</style>
